import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import RecaptchaCheckbox from "./RecaptchaCheckbox";
import OpeningHoursLocationPicker from "./OpeningHoursLocationPicker";
import { normaliseMapPosition } from "./locationUtils";
import "./openingHours.css";

const weekdayDefinitions = [
  ["Monday", "mon"],
  ["Tuesday", "tue"],
  ["Wednesday", "wed"],
  ["Thursday", "thu"],
  ["Friday", "fri"],
];

const updateEndpoint =
  "https://tracking.fhbrundle.co.uk/trackapi?cmd=openinghoursedit&new=true";

function formatOpeningTime(value) {
  const match = String(value || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function normaliseLocation(entry) {
  return normaliseMapPosition(
    entry?.cdr_latitude,
    entry?.cdr_longitude
  );
}

function normaliseOpeningHours(value) {
  const entries = Array.isArray(value) ? value : value ? [value] : [];
  const entry = entries.find(
    (candidate) => candidate && typeof candidate === "object"
  );

  if (!entry) return null;

  return {
    days: weekdayDefinitions.map(([label, key]) => {
      const closed =
        String(entry[`${key}_closed`] || "").toUpperCase() === "Y";
      const opens = formatOpeningTime(entry[`${key}_open`]);
      const closes = formatOpeningTime(entry[`${key}_close`]);

      return {
        key,
        label,
        closed,
        opens,
        closes,
        display: closed
          ? "Closed"
          : opens && closes
          ? `${opens} - ${closes}`
          : "Hours not provided",
      };
    }),
    vehicleType:
      String(entry.vehicle_type || "").trim().toUpperCase() || "ANY",
    emailAllowed: String(entry.cdr_email_allow ?? "1") !== "0",
    mobileAllowed: String(entry.cdr_mobile_allow ?? "1") !== "0",
    deliveryCode: String(entry.cdr_delcode ?? "0").trim() || "0",
    location: normaliseLocation(entry),
  };
}

function createEditableDays(openingHours) {
  return openingHours.days.map((day) => ({
    ...day,
    opens: day.opens || "07:00",
    closes: day.closes || (day.key === "fri" ? "12:00" : "16:30"),
  }));
}

function groupDays(days) {
  return days.reduce((result, day) => {
    const previous = result[result.length - 1];
    if (previous?.display === day.display) {
      previous.end = day.label;
    } else {
      result.push({
        start: day.label,
        end: day.label,
        display: day.display,
      });
    }
    return result;
  }, []);
}

async function sendOpeningHoursUpdate({
  reference,
  customer,
  deliveryCode,
  days,
  vehicleType,
  emailAllowed,
  mobileAllowed,
  notes,
  location,
  recaptchaToken,
}) {
  const openingHours = Object.fromEntries(
    days.map((day) => [
      day.key,
      {
        open: day.opens,
        close: day.closes,
        closed: day.closed ? "Y" : "N",
      },
    ])
  );

  let response;
  try {
    response = await fetch(updateEndpoint, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tracking_num: reference,
        customer,
        del_code: deliveryCode || "0",
        opening_hours: openingHours,
        vehicle_type: vehicleType,
        email_allow: emailAllowed ? 1 : 0,
        mobile_allow: mobileAllowed ? 1 : 0,
        source_text: String(notes || "").trim() || null,
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
        recaptcha_token: recaptchaToken,
        requested_at: new Date().toISOString(),
      }),
    });
  } catch {
    throw new Error(
      "Your opening-hours amendment could not be sent. Check your connection and try again."
    );
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    if (response.ok) return { success: true };
  }

  if (!response.ok || payload?.success === false || payload?.valid === false) {
    throw new Error(
      payload?.message || "Your opening-hours amendment could not be saved."
    );
  }

  return payload || { success: true };
}

export default function OpeningHours({
  value,
  customer,
  deliveryCode,
  reference,
}) {
  const openingHours = useMemo(() => normaliseOpeningHours(value), [value]);
  const [isOpen, setIsOpen] = useState(false);
  const [days, setDays] = useState([]);
  const [vehicleType, setVehicleType] = useState("ANY");
  const [emailAllowed, setEmailAllowed] = useState(true);
  const [mobileAllowed, setMobileAllowed] = useState(true);
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [requestState, setRequestState] = useState("idle");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setSubmitted(false);
    setIsOpen(false);
  }, [reference]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && requestState !== "loading") {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, requestState]);

  if (!openingHours) return null;

  const groups = groupDays(openingHours.days);

  const openEditor = () => {
    setDays(createEditableDays(openingHours));
    setVehicleType(
      openingHours.vehicleType.includes("TRANSIT") ? "TRANSIT ONLY" : "ANY"
    );
    setEmailAllowed(openingHours.emailAllowed);
    setMobileAllowed(openingHours.mobileAllowed);
    setNotes("");
    setLocation(
      String(openingHours.deliveryCode) === String(deliveryCode ?? "0")
        ? openingHours.location
        : null
    );
    setRecaptchaToken("");
    setRequestState("idle");
    setMessage("");
    setIsOpen(true);
  };

  const updateDay = (dayIndex, changes) => {
    setDays((currentDays) =>
      currentDays.map((day, index) =>
        index === dayIndex ? { ...day, ...changes } : day
      )
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    const invalidDay = days.find(
      (day) =>
        !day.closed &&
        (!day.opens || !day.closes || day.closes <= day.opens)
    );
    if (invalidDay) {
      setRequestState("error");
      setMessage(
        `${invalidDay.label} closing time must be after its opening time.`
      );
      return;
    }

    if (!customer) {
      setRequestState("error");
      setMessage("The customer reference is missing from this quote.");
      return;
    }

    if (!recaptchaToken) {
      setRequestState("error");
      setMessage("Confirm that you're not a robot before sending the update.");
      return;
    }

    setRequestState("loading");
    try {
      const response = await sendOpeningHoursUpdate({
        reference,
        customer,
        deliveryCode: openingHours.deliveryCode || deliveryCode,
        days,
        vehicleType,
        emailAllowed,
        mobileAllowed,
        notes,
        location,
        recaptchaToken,
      });
      setRequestState("success");
      setMessage(
        response?.message ||
          "Your hours and notification preferences have been sent for review."
      );
      setSubmitted(true);
    } catch (error) {
      setRequestState("error");
      setMessage(error.message);
      setCaptchaReset((value) => value + 1);
    }
  };

  const modal = isOpen
    ? createPortal(
        <div
          className="opening-hours-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              requestState !== "loading"
            ) {
              setIsOpen(false);
            }
          }}
        >
          <section
            className="opening-hours-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="opening-hours-dialog-heading"
          >
            <button
              type="button"
              className="opening-hours-dialog__close"
              aria-label="Close opening hours editor"
              onClick={() => setIsOpen(false)}
              disabled={requestState === "loading"}
            >
              &times;
            </button>

            {requestState === "success" ? (
              <div className="opening-hours-dialog__success" role="status">
                <span aria-hidden="true">&#10003;</span>
                <h2 id="opening-hours-dialog-heading">Amendment sent</h2>
                <p>{message}</p>
                <p>
                  The displayed details will remain unchanged until the update
                  is reviewed.
                </p>
                <button type="button" onClick={() => setIsOpen(false)}>
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="opening-hours-dialog__heading">
                  <p className="eyebrow">Customer details</p>
                  <h2 id="opening-hours-dialog-heading">
                    Amend hours and notification preferences
                  </h2>
                  <p>
                    Check the hours, notification preferences and vehicle
                    access, then add any notes for our team.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <fieldset className="opening-hours-editor">
                    <legend>Opening times</legend>
                    {days.map((day, dayIndex) => (
                      <div className="opening-hours-editor__day" key={day.key}>
                        <strong>{day.label}</strong>
                        <label>
                          <span>Opens</span>
                          <input
                            type="time"
                            value={day.opens}
                            onChange={(event) =>
                              updateDay(dayIndex, {
                                opens: event.target.value,
                              })
                            }
                            disabled={
                              day.closed || requestState === "loading"
                            }
                            required={!day.closed}
                          />
                        </label>
                        <label>
                          <span>Closes</span>
                          <input
                            type="time"
                            value={day.closes}
                            onChange={(event) =>
                              updateDay(dayIndex, {
                                closes: event.target.value,
                              })
                            }
                            disabled={
                              day.closed || requestState === "loading"
                            }
                            required={!day.closed}
                          />
                        </label>
                        <label className="opening-hours-editor__closed">
                          <input
                            type="checkbox"
                            checked={day.closed}
                            onChange={(event) =>
                              updateDay(dayIndex, {
                                closed: event.target.checked,
                              })
                            }
                            disabled={requestState === "loading"}
                          />
                          <span>Closed</span>
                        </label>
                      </div>
                    ))}
                  </fieldset>

                  <section
                    className="opening-hours-editor notification-preferences"
                    aria-labelledby="notification-preferences-heading"
                  >
                    <h3 id="notification-preferences-heading">
                      Notification preferences
                    </h3>
                    <p>
                      These preferences apply to all of your delivery addresses.
                    </p>
                    <label>
                      <input
                        type="checkbox"
                        checked={emailAllowed}
                        onChange={(event) =>
                          setEmailAllowed(event.target.checked)
                        }
                        disabled={requestState === "loading"}
                      />
                      <span>
                        <strong>Email notifications</strong>
                        <small>
                          Receive order and delivery updates by email.
                        </small>
                      </span>
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={mobileAllowed}
                        onChange={(event) =>
                          setMobileAllowed(event.target.checked)
                        }
                        disabled={requestState === "loading"}
                      />
                      <span>
                        <strong>Mobile notifications</strong>
                        <small>
                          Receive order and delivery updates on mobile.
                        </small>
                      </span>
                    </label>
                  </section>

                  <label className="opening-hours-dialog__field">
                    <span>Vehicle access</span>
                    <select
                      value={vehicleType}
                      onChange={(event) => setVehicleType(event.target.value)}
                      disabled={requestState === "loading"}
                    >
                      <option value="ANY">Any</option>
                      <option value="TRANSIT ONLY">Transit only</option>
                    </select>
                  </label>

                  <OpeningHoursLocationPicker
                    value={location}
                    disabled={requestState === "loading"}
                    onChange={setLocation}
                  />

                  <label className="opening-hours-dialog__field">
                    <span>Notes (optional)</span>
                    <textarea
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      rows="4"
                      maxLength="2000"
                      placeholder="Add access information or anything else our team should know"
                      disabled={requestState === "loading"}
                    />
                    <small>{notes.length}/2000</small>
                  </label>

                  <RecaptchaCheckbox
                    onChange={setRecaptchaToken}
                    resetSignal={captchaReset}
                  />

                  {message && (
                    <p className="opening-hours-dialog__error" role="alert">
                      {message}
                    </p>
                  )}

                  <div className="opening-hours-dialog__actions">
                    <button
                      type="button"
                      className="button-secondary"
                      onClick={() => setIsOpen(false)}
                      disabled={requestState === "loading"}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        requestState === "loading" || !recaptchaToken
                      }
                    >
                      {requestState === "loading"
                        ? "Sending..."
                        : "Send for review"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </section>
        </div>,
        document.body
      )
    : null;

  return (
    <section className="opening-hours" aria-labelledby="opening-hours-heading">
      <div className="opening-hours__heading">
        <h3 id="opening-hours-heading">Delivery hours</h3>
        <button type="button" onClick={openEditor} disabled={submitted}>
          {submitted ? "Amendment sent" : "Amend"}
        </button>
      </div>
      <dl>
        {groups.map((group) => (
          <div key={group.start}>
            <dt>
              {group.start === group.end
                ? group.start
                : `${group.start} to ${group.end}`}
            </dt>
            <dd>{group.display}</dd>
          </div>
        ))}
        <div className="opening-hours__preference">
          <dt>Vehicle access</dt>
          <dd>
            {openingHours.vehicleType.includes("TRANSIT")
              ? "Transit only"
              : "Any vehicle"}
          </dd>
        </div>
      </dl>
      {submitted && (
        <p className="opening-hours__submitted" role="status">
          Your amendment is awaiting review.
        </p>
      )}
      {modal}
    </section>
  );
}
